/* eslint-disable no-restricted-syntax */
import { Placement } from '@popperjs/core';
import clsx from 'clsx';
import React, {
  FC,
  ReactNode,
  StrictMode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { usePopper } from 'react-popper';

import Typography from '../common/typography';

type Props = {
  children: ReactNode;
  popperBody: ReactNode;
  placement?: Placement;
  /** Optional and will override the internal state of the poppper wrapper */
  isPopperOpen?: boolean;
  /** Optional and will override the internal state of the poppper wrapper */
  setIsPopperOpen?: (isOpen: boolean) => void;
  offset?: [number, number];
  /**
   * Pin will be a toggle with and have an "x" in the decorators.
   * Hover will only trigger on hover and not have an "x" to close
   * */
  variant?: 'pin' | 'hover';
};

const PopperTooltipWrapper: FC<Props> = ({
  children,
  popperBody,
  isPopperOpen = undefined,
  offset = [0, 8],
  placement = 'top',
  setIsPopperOpen = undefined,
  variant = 'pin',
}) => {
  const [isExpanded, setIsExpanded] = useState(isPopperOpen ?? false);
  const [isHovered, setIsHovered] = useState(false);
  /** The popper element */
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  /** The element that the popper is based around */
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);

  /**
   * Used just to trigger a re-render.
   * Will increment every time the parent node with the attribute 'data-popper-container-resizes
   * */
  const [resizeCounter, setResizeCounter] = useState<number>(0);

  /** Popper settings */
  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers: [
        {
          enabled: true,
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          name: 'arrow',
          options: {
            element: arrowElement,
          },
        },
      ],
      placement,
    }
  );

  /** Toggles the pooper and updates the UI */
  const handleOnPopperToggle = useCallback(
    (isVisible: boolean) => {
      // Will use the prop if available, otherwise will use internal
      if (setIsPopperOpen) {
        setIsPopperOpen(isVisible);
      } else {
        setIsExpanded(isVisible);
      }

      // https://stackoverflow.com/questions/65585859/react-popper-incorrect-position-on-mount
      // popper docs suck
      update?.();
    },
    [setIsPopperOpen, update]
  );

  /**
   * Adds a resize observer on the closest node with `data-popper-container` to detect size changes.
   * Will update the tooltip position on size changes
   * */
  useEffect(() => {
    const target = referenceElement;

    // Finds the closest parent with this attribute
    const dataPopperNode = target?.closest('[data-popper-container]');

    const resizeObserver = new ResizeObserver(() => {
      setResizeCounter((prev) => prev + 1);
    });

    if (dataPopperNode) {
      resizeObserver.observe(dataPopperNode);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [referenceElement]);

  /** Will update the position of the tooltip every time the position of the tooltip changes */
  useEffect(() => {
    update?.();
  }, [resizeCounter, update]);

  /** Will update the internal state of the popper if an override is passed through */
  useEffect(() => {
    if (isPopperOpen !== undefined) {
      setIsExpanded(isPopperOpen);
    }
  }, [isPopperOpen]);

  /** Make sure that the user passes in either both or neither overrides */
  useEffect(() => {
    // If at least one is passed in, then both need to be passed in
    if (
      (isPopperOpen !== undefined || !!setIsPopperOpen) &&
      !(isPopperOpen !== undefined && !!setIsPopperOpen)
    ) {
      throw Error(
        'You need to pass in both isPopperOpen and setIsPopperOpen if you provided at least one of them'
      );
    }
  }, [isPopperOpen, setIsPopperOpen]);

  // Updating the expanded state
  useEffect(() => {
    if (variant === 'hover') {
      setIsExpanded(isHovered);
    }
  }, [isHovered, variant]);

  return (
    <StrictMode>
      <div className="flex flex-col items-center">
        <button
          ref={(ref) => setReferenceElement(ref)}
          className={clsx(isExpanded && 'underline', 'hover:underline')}
          type="button"
          onClick={() => handleOnPopperToggle(true)}
          // Required for accessibility (eslint), but no design for it
          onFocus={() => {}}
          onMouseLeave={() => setIsHovered(false)}
          onMouseOver={() => setIsHovered(true)}
        >
          {children}
        </button>

        {/* Popper */}
        {isExpanded && (
          <div
            ref={(ref) => setPopperElement(ref)}
            className={clsx(
              'z-[100] -mx-4 max-w-[280px] bg-gray-80 px-4 pt-3',
              variant === 'pin' ? 'pb-6' : 'pb-3'
            )}
            // id is required so it can be referenced from globals.css
            id="popper-tooltip"
            style={styles.popper}
            {...attributes.popper}
          >
            {/* Decorator */}
            {variant === 'pin' && (
              <div className="mb-3 flex items-center justify-end">
                <button
                  className="flex items-center justify-center text-white"
                  type="button"
                  onClick={() => handleOnPopperToggle(false)}
                >
                  <span className="material-icons">close</span>
                </button>
              </div>
            )}

            <Typography className="text-white">{popperBody}</Typography>

            <div
              ref={(ref) => setArrowElement(ref)}
              // id is required so it can be referenced from globals.css
              id="popper-arrow"
              style={styles.arrow}
            />
          </div>
        )}
      </div>
    </StrictMode>
  );
};

export default PopperTooltipWrapper;
